import React from "react";

// Navigate
import { useNavigate, useParams } from "react-router-dom";

// Mui
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// Mui Styles
import { StyledButton, StyledPaper } from "@/styles/mui/globalStyles";
import { createTheme, styled, ThemeProvider } from "@mui/material/styles";

// Components
import ProductAutoComplete from "@/components/Filter/productAutocomplete";
import Layout from "@/components/layout";
import TargetRow from "@/components/PromotionTarget/targetRow";
import Title from "@/components/title";
import { getManningsProducts } from "@/fetch/fetchProduct";
import {
  downLoadTemplate,
  getPromotionShopTarget,
  getPromotionTargetById,
  updatePromotionTarget,
} from "@/fetch/fetchPromotionTarget";
import { getRegionOptions } from "@/fetch/fetchWork";
import extractPromotionTarget from "@/lib/extractPromotionTarget";

import _ from "lodash";
import moment from "moment";

// Redux
import { useCurrentAction } from "@/hooks/user";
import { Download, Upload } from "@mui/icons-material";
import { Button, Select, Stack } from "@mui/material";

const PromotionTargetEditPage = (props) => {
  const { open, setOpen } = props;
  const actions = useCurrentAction();
  const { update, createTarget } = actions;
  const { id } = useParams();

  // Navigate and Location and Params
  const navigate = useNavigate();

  // States
  const [formData, setFormData] = React.useState({
    removed_shop_targets: [],
    removed_shop: [],
  });
  const [disableUpdate, setDisableUpdate] = React.useState(false);
  const [shopOptions, setShopOptions] = React.useState([]);
  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [productOptions, setProductOptions] = React.useState([]);
  const [slideTranOn, setSlideTranOn] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            marginBottom: 10,
          },
        },
      },
    },
  });
  const setShopTarget = (index, field, value) => {
    const promotion_shop_targets = [...formData.promotion_shop_targets];
    if (field === "shop") {
      const removed = _.difference(promotion_shop_targets[index][field], value);
      const removed_shop_targets = [
        ...formData.removed_shop_targets,
        ...removed,
      ];

      promotion_shop_targets[index][field] = value;
      setFormData({
        ...formData,
        promotion_shop_targets,
        removed_shop_targets: _.filter(
          removed_shop_targets,
          (r) => !_.includes(value, r)
        ),
      });
    } else {
      promotion_shop_targets[index][field] = value;

      setFormData({
        ...formData,
        promotion_shop_targets,
      });
    }
  };
  // Submit
  const onClickSubmit = async () => {
    try {
      await updatePromotionTarget(id, formData);
      setSnackSeverity("success");
      setSnackMessage("更新成功");
      setTimeout(() => {
        setSnackOpen(false);
        navigate(-1);
      }, 1000);
      setSnackOpen(true);
    } catch {
      setSnackSeverity("error");
      setSnackMessage("更新失敗");
      setSnackOpen(true);
    }
  };
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });
  const handleDownload = async () => {
    const response = await downLoadTemplate();
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Template ${formData.name}.xlsx`);
    document.body.appendChild(link);
    link.click();
    setSnackMessage("下載成功");
    setSnackOpen(true);
    setSnackSeverity("success");
  };
  const handleImport = async (e) => {
    let confirm = true;
    if (formData.promotion_shop_targets.length > 0) {
      confirm = window.confirm(`上載會取代原有目標，要繼續嗎？`);
    }
    const promotion_shop_targets = [];
    if (confirm) {
      const data = await extractPromotionTarget(e);
      _.forEach(data, (d) => {
        const { shop_id } = _.find(
          shopOptions,
          (shopOption) => shopOption.work_shortcode === d.work_shortcode
        );
        promotion_shop_targets.push({
          target_shop_name: d.target_shop_name,
          shop: [shop_id],
          target_amount: d.target_amount,
        });
        setFormData({
          ...formData,
          promotion_shop_targets,
        });
      });
    }
    e.target.value = null;
  };
  const handleAddRow = () => {
    const promotion_shop_targets = formData.promotion_shop_targets
      ? [...formData.promotion_shop_targets]
      : [];
    promotion_shop_targets.push({ shop: [], target_amount: 0 });
    setFormData({
      ...formData,
      promotion_shop_targets,
    });
  };
  const handleRemoveRow = (removeIndex) => {
    const promotion_shop_targets = [...formData.promotion_shop_targets];
    const removed = _.remove(
      promotion_shop_targets,
      (promotion_shop_target, index) => index === removeIndex
    );
    setFormData({
      ...formData,
      promotion_shop_targets,
      removed_shop_targets: [
        ...formData.removed_shop_targets,
        ...removed[0].shop,
      ],
    });
  };
  const fetchAll = async () => {
    const { data: products } = await getManningsProducts(false);
    setProductOptions(products);

    const { data: promotion_target } = await getPromotionTargetById(id);
    const { data: locations } = await getRegionOptions();
    const { data: promotion_shop_targets } = await getPromotionShopTarget(id);
    const groupByAms = _.groupBy(promotion_shop_targets, "alias");
    setDisableUpdate(moment().isAfter(promotion_target.date, "day") || !update);
    setShopOptions(locations);
    setFormData({
      ...promotion_target,
      promotion_shop_targets: createTarget ? promotion_shop_targets : [],
      removed_shop_targets: [],
      group_by_ams: groupByAms,
      date: moment(promotion_target.date).format("YYYY-MM-DD"),
    });
    setSlideTranOn(true);
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  return (
    <Layout
      header="workLocationManageDetailPage"
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      selectedIndex={17}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title title={`修改推廣目標`} history={() => navigate(-1)} />
      <Fade in={slideTranOn} mountOnEnter unmountOnExit>
        <StyledPaper page={"yes"}>
          <ThemeProvider theme={theme}>
            <Box validate="true" sx={{ width: 1, flex: 1 }}>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                    lg: 3,
                  }}
                >
                  <TextField
                    disabled={disableUpdate}
                    id="name"
                    name="name"
                    label="名稱"
                    value={formData.name}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        [event.target.name]: event.target.value,
                      });
                    }}
                    sx={{ width: "100%" }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                    lg: 3,
                  }}
                >
                  <DatePicker
                    disabled={disableUpdate}
                    id="date"
                    name="date"
                    label="日期"
                    value={formData.date}
                    minDate={!disableUpdate ? moment() : null}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        date: moment(value).format("YYYY-MM-DD"),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: "100%" }} />
                    )}
                  ></DatePicker>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                    lg: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="target-type-label">目標類型</InputLabel>
                    <Select
                      disabled={disableUpdate}
                      fullWidth
                      labelId="target-type-label"
                      label="目標類型"
                      value={formData.type}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          type: e.target.value,
                        });
                      }}
                    >
                      <MenuItem value={"qty"}>數量</MenuItem>
                      <MenuItem value={"amount"}>銷售數</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                    lg: 3,
                  }}
                >
                  {formData.type === "qty" && (
                    <ProductAutoComplete
                      disabled={disableUpdate}
                      multiple
                      fullWidth
                      sx={{ flex: 1 }}
                      productOptions={productOptions}
                      handleOnChange={(value) => {
                        setFormData({
                          ...formData,
                          product_ids: value,
                        });
                      }}
                      selectedValue={formData.product_ids ?? []}
                    ></ProductAutoComplete>
                  )}
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                  }}
                >
                  {createTarget ? (
                    <div>
                      {_.map(
                        formData.promotion_shop_targets,
                        (shopTarget, index) => {
                          const selectedShop = [];
                          _.forEach(
                            formData.promotion_shop_targets,
                            (promotion_shop_targets, otherIndex) => {
                              if (otherIndex !== index)
                                selectedShop.push(
                                  ...promotion_shop_targets.shop
                                );
                            }
                          );
                          return (
                            <TargetRow
                              disabled={false}
                              index={index}
                              setShopTarget={setShopTarget}
                              selectedShop={selectedShop}
                              handleRemoveRow={handleRemoveRow}
                              shopTarget={shopTarget}
                              shopOptions={shopOptions}
                            ></TargetRow>
                          );
                        }
                      )}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <Button
                          onClick={() => handleDownload()}
                          variant="contained"
                        >
                          <Download></Download>下載範本
                        </Button>
                        <Button
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          startIcon={<Upload />}
                        >
                          上載
                          <VisuallyHiddenInput
                            type="file"
                            onChange={(event) => handleImport(event)}
                            multiple
                          />
                        </Button>

                        <Button
                          onClick={() => handleAddRow()}
                          variant="contained"
                        >
                          <AddIcon></AddIcon>新增目標
                        </Button>
                      </Stack>
                    </div>
                  ) : (
                    <div>
                      {_.map(formData.group_by_ams, (group_by_am, alias) => {
                        return (
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                            >
                              <Typography component="span">{alias}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {_.map(group_by_am, (g, index) => {
                                return (
                                  <TargetRow
                                    disabled={true}
                                    viewOnly={true}
                                    index={index}
                                    setShopTarget={setShopTarget}
                                    handleRemoveRow={handleRemoveRow}
                                    handleAddRow={handleAddRow}
                                    shopTarget={g}
                                    shopOptions={shopOptions}
                                    isLastRow={
                                      index ===
                                      formData.promotion_shop_targets.length - 1
                                    }
                                  ></TargetRow>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </div>
                  )}
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                  }}
                >
                  <Grid justifyContent={"flex-end"} container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 12,
                        lg: 2,
                      }}
                    >
                      <StyledButton
                        variant="contained"
                        size="small"
                        onClick={onClickSubmit}
                      >
                        <Typography variant="subtitle1" noWrap component="div">
                          確認
                        </Typography>
                      </StyledButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </ThemeProvider>
        </StyledPaper>
      </Fade>
    </Layout>
  );
};

export default PromotionTargetEditPage;
